import React from 'react'
import { Box, Chip, Paper, Typography } from '@material-ui/core'
import {
	CAMPAIGN_ACCENT_BORDER,
	CAMPAIGN_ACCENT_ORANGE,
	G,
	GL,
} from '../CampaignModalSkeleton.styles'

export function RuleSummaryMechanicsRow({
	children,
}: {
	children: React.ReactNode
}) {
	return (
		<Box
			display="flex"
			flexWrap="wrap"
			alignItems="center"
			gridGap={10}
			mb={1.5}
		>
			{children}
		</Box>
	)
}

export interface RuleSummaryStatBadgeProps {
	value: React.ReactNode
	caption: string
	tone?: 'primary' | 'accent'
	minWidth?: number
}

export function RuleSummaryStatBadge({
	value,
	caption,
	tone = 'primary',
	minWidth = 80,
}: RuleSummaryStatBadgeProps) {
	const accent = tone === 'accent'
	return (
		<Box
			style={{
				padding: '10px 16px',
				background: accent ? '#fff7ed' : GL,
				border: accent ? `1px solid ${CAMPAIGN_ACCENT_BORDER}` : undefined,
				borderRadius: 10,
				textAlign: 'center',
				minWidth,
			}}
		>
			<Typography
				style={{
					fontSize: 22,
					fontWeight: 700,
					color: accent ? CAMPAIGN_ACCENT_ORANGE : G,
				}}
			>
				{value}
			</Typography>
			<Typography
				variant="caption"
				style={{ color: '#757575', display: 'block', marginTop: 2 }}
			>
				{caption}
			</Typography>
		</Box>
	)
}

export function RuleSummaryArrow() {
	return <Typography style={{ color: '#9e9e9e' }}>→</Typography>
}

function uniqueNames(names: string[]) {
	return [...new Set(names.map((n) => n.trim()).filter(Boolean))]
}

export interface RuleSummaryEligibleProductsProps {
	title?: string
	names: string[]
	variant?: 'default' | 'review'
}

/**
 * Caption + outlined chips (matches reference modal “Produits éligibles” block).
 */
export function RuleSummaryEligibleProducts({
	title = 'Produits éligibles',
	names,
	variant = 'default',
}: RuleSummaryEligibleProductsProps) {
	const list = uniqueNames(names)
	const isReview = variant === 'review'

	return (
		<Box>
			<Typography
				variant={isReview ? 'body2' : 'caption'}
				style={{
					fontWeight: isReview ? 600 : 500,
					color: isReview ? '#212121' : '#424242',
					marginBottom: isReview ? 8 : 6,
					display: 'block',
					fontSize: isReview ? 14 : undefined,
				}}
			>
				{title}
				<span style={{ marginLeft: 6, color: isReview ? '#212121' : '#9e9e9e', fontWeight: isReview ? 600 : 400 }}>
					({list.length})
				</span>
			</Typography>
			<Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
				{list.map((n) => (
					<Chip
						key={n}
						label={n}
						size="small"
						variant="outlined"
						style={{
							borderColor: G,
							color: G,
							fontWeight: 500,
							backgroundColor: '#fff',
						}}
					/>
				))}
			</Box>
		</Box>
	)
}

const REVIEW_CARD_BORDER = '1px solid rgba(0,0,0,0.12)'

export function CampaignReviewTopRow({
	left,
	right,
}: {
	left: React.ReactNode
	right: React.ReactNode
}) {
	return (
		<Box
			display="flex"
			flexDirection={{ xs: 'column', md: 'row' }}
			alignItems="stretch"
			width="100%"
			style={{ gap: 16 }}
		>
			<Box
				width={{ xs: '100%', md: '40%' }}
				flexShrink={0}
				minWidth={0}
				display="flex"
				flexDirection="column"
			>
				{left}
			</Box>
			<Box
				flex={1}
				minWidth={0}
				display="flex"
				flexDirection="column"
				style={{ gap: 12 }}
			>
				{right}
			</Box>
		</Box>
	)
}

export interface CampaignPreviewCardProps {
	imageSlot: React.ReactNode
	title: string
	summary: string
}

/** Preview card: image area is whatever the caller passes (img, cropper, placeholder…). */
export function CampaignPreviewCard({
	imageSlot,
	title,
	summary,
}: CampaignPreviewCardProps) {
	return (
		<Paper
			variant="outlined"
			style={{
				width: '100%',
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 10,
				overflow: 'hidden',
				border: REVIEW_CARD_BORDER,
			}}
		>
			<Box
				style={{
					width: '100%',
					flex: 1,
					padding: 12,
					backgroundColor: '#eceff1',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'visible',
					position: 'relative',
					boxSizing: 'border-box',
				}}
			>
				{imageSlot}
			</Box>
			<Box
				flexShrink={0}
				style={{
					padding: '12px 16px',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
				}}
			>
				<Typography
					style={{
						fontSize: 11,
						fontWeight: 600,
						color: '#9e9e9e',
						letterSpacing: '0.08em',
						textTransform: 'uppercase',
						marginBottom: 6,
					}}
				>
					APERÇU CAMPAGNE
				</Typography>
				<Typography
					style={{
						fontSize: 18,
						fontWeight: 700,
						color: '#212121',
						lineHeight: 1.25,
						marginBottom: 4,
						wordBreak: 'break-word',
					}}
				>
					{title}
				</Typography>
				<Typography
					style={{
						fontSize: 14,
						fontWeight: 500,
						color: G,
						lineHeight: 1.4,
					}}
				>
					{summary}
				</Typography>
			</Box>
		</Paper>
	)
}
